// Восстановление после провала загрузки ленивых чанков при устаревшем SPA-shell.
//
// Сценарий (прод-Rollbar 2026-07-06): вкладка открыта долго → передеплой фронта →
// хеш ленивого чанка сменился → `js/tasks.<hash>.js` на сервере нет → SPA-fallback
// отдаёт index.html со статусом 200 → браузер парсит `<!DOCTYPE html>` как JS →
// `SyntaxError: Unexpected token '<'`. Прилетает и как router.onError (rejected import),
// и как UNCAUGHT window "error" (когда webpack не увидел import как rejected —
// сервер вернул 200 + HTML). Оба пути ведут к одному guarded-reload.
//
// Функции здесь — чистые предикаты + guard над sessionStorage, покрыты unit-тестом.
// Сам `window.location.reload()` живёт в тонкой обёртке ниже и юнитом не покрывается.

// Матч сообщения ошибки роутера (rejected dynamic import ленивого чанка).
// Покрывает webpack ("Loading chunk N failed", "Loading CSS chunk"), обобщённый
// ChunkLoadError и характерный "Unexpected token '<'" (HTML вместо JS).
const ROUTER_CHUNK_RE = /Loading( CSS)? chunk .* failed|ChunkLoadError|Unexpected token '<'/;

export function isStaleChunkRouterError(err) {
  if (!err) return false;
  const msg = (err.message || String(err)) || "";
  const name = err.name || "";
  return name === "ChunkLoadError" || ROUTER_CHUNK_RE.test(msg);
}

// Узкий матч для UNCAUGHT window "error": ошибка при исполнении файла из /js/*.js
// (наш чанк/бандл), которая является SyntaxError либо буквально "Unexpected token '<'".
// Узость намеренна: не реагируем на ошибки не из /js/ и на прочие рантайм-ошибки
// главного бандла — иначе рискуем поймать петлю на невосстановимой ошибке.
export function isStaleChunkWindowError(event) {
  if (!event) return false;
  const filename = event.filename || "";
  if (!/\/js\/.+\.js(\?|$)/.test(filename)) return false;
  const message = event.message || "";
  const errorName = (event.error && event.error.name) || "";
  return errorName === "SyntaxError" || /Unexpected token '<'/.test(message);
}

// Guard от петли перезагрузок: разрешаем reload не чаще раза в MIN_INTERVAL_MS.
// Таймстамп в sessionStorage переживает reload в рамках той же вкладки, поэтому
// если сам shell/index.html битый и ошибка повторяется сразу после reload —
// второй reload будет заблокирован (петли не будет).
export const RELOAD_GUARD_KEY = "spaStaleChunkReloadAt";
export const MIN_RELOAD_INTERVAL_MS = 10000;

export function shouldReloadForStaleChunk(storage, now, minIntervalMs = MIN_RELOAD_INTERVAL_MS) {
  try {
    const raw = storage.getItem(RELOAD_GUARD_KEY);
    const last = raw ? parseInt(raw, 10) : 0;
    if (last && !Number.isNaN(last) && now - last < minIntervalMs) {
      return false; // недавно уже перезагружались — не крутим петлю
    }
    storage.setItem(RELOAD_GUARD_KEY, String(now));
    return true;
  } catch (e) {
    return false; // storage недоступен → лучше не реагировать, чем рискнуть петлёй
  }
}

// Сброс guard после успешной навигации: восстановление должно срабатывать и на
// будущих деплоях, а не «сгорать» после первого раза за сессию.
export function clearReloadGuard(storage) {
  try {
    storage.removeItem(RELOAD_GUARD_KEY);
  } catch (e) {
    /* noop */
  }
}

// Тонкая обёртка над guard + window.location.reload() (side-effect, не под юнитом).
// Возвращает true, если reload был инициирован.
export function reloadOnceForStaleChunk() {
  if (typeof window === "undefined" || !window.sessionStorage || !window.location) return false;
  if (!shouldReloadForStaleChunk(window.sessionStorage, Date.now())) return false;
  window.location.reload();
  return true;
}
